/**
 * Project knowledge: what `knowledge_search` may return, and how much pinned knowledge is
 * allowed into the governed prompt.
 */
export const KNOWLEDGE_SEARCH_MAX_HITS = 20;
export const KNOWLEDGE_SEARCH_DEFAULT_HITS = 10;
/** Rows read from each branch before fusion; the fused list is then cut to the hit limit. */
export const KNOWLEDGE_SEARCH_CANDIDATE_FANOUT = 60;
/**
 * Pinned knowledge is composed into every prompt on the profiles that opt in, so its cost is
 * paid on every turn. Newest items win both caps: a team that pins past these limits keeps
 * seeing what it pinned most recently, and the Knowledge screen shows the pinned count.
 */
export const PINNED_KNOWLEDGE_MAX_ITEMS = 40;
export const PINNED_KNOWLEDGE_MAX_CHARS = 4000;
/**
 * How far back the pinned loader scans the project's knowledge before giving up on finding
 * more pinned items. Pinned is a rare flag with no index of its own, so the read is bounded by
 * rows examined rather than by rows returned.
 */
export const PINNED_KNOWLEDGE_SCAN_LIMIT = 400;
/**
 * The pinned term lists composed into the prompt beside those statements, on their own budget.
 *
 * A list is spelling rather than reasoning, so it is bounded by the characters its terms
 * actually spend rather than by an item count alone, and the two budgets are separate so a
 * project that pins many statements still gets its vocabulary. Newest lists win the count cap;
 * the list that runs past the character cap keeps its head and says so with an ellipsis, because
 * a list dropped whole takes spellings nobody can guess with it.
 */
export const PINNED_DICTIONARY_MAX_LISTS = 20;
export const PINNED_DICTIONARY_MAX_CHARS = 3000;
/**
 * Term lists one `knowledge_search` call may report, capped apart from the statement hits so a
 * project with many lists can never push a statement out of the answer.
 */
export const KNOWLEDGE_SEARCH_MAX_DICTIONARY_HITS = 5;
/**
 * Terms one reported list carries. A list holds up to 500 spellings and a tool result is graded
 * on its widest array, so an uncapped list would demote the whole payload — statements included.
 */
export const KNOWLEDGE_SEARCH_DICTIONARY_MAX_TERMS = 40;
/**
 * Every source kind, keyed exhaustively so a new arm on {@link KnowledgeEvidenceSource} cannot
 * be added without naming it here. Declaration order is the order the filter offers them in.
 */
const KNOWLEDGE_SOURCE_KIND_DEFINITIONS = {
    document: { label: "Document", available: true },
    decision: { label: "Decision in chat", available: true },
    clarification: { label: "Clarification request", available: true },
    manual: { label: "Entered manually", available: true },
    organization: { label: "Organization", available: false },
    folder: { label: "Folder", available: false },
};
/**
 * The source vocabulary as one ordered list.
 *
 * The filter renders the available entries rather than the kinds the current page happens to
 * cite: a source axis that only names what is already there cannot tell a reader that a project
 * has nothing from a document yet, and picking one kind would leave it as the only choice.
 */
export const KNOWLEDGE_SOURCE_KINDS = Object.entries(KNOWLEDGE_SOURCE_KIND_DEFINITIONS).map(([id, entry]) => ({ id: id, ...entry }));
/** The source kinds something can actually produce today. */
export const AVAILABLE_KNOWLEDGE_SOURCE_KINDS = KNOWLEDGE_SOURCE_KINDS.filter((entry) => entry.available);
export function knowledgeSourceKindLabel(kind) {
    return KNOWLEDGE_SOURCE_KIND_DEFINITIONS[kind].label;
}
export function summarizeKnowledgeSources(sources, keys = []) {
    const kinds = [];
    const documents = [];
    let authorName;
    let authorUserId;
    let authoredAt;
    for (const [index, source] of sources.entries()) {
        if (!kinds.includes(source.kind))
            kinds.push(source.kind);
        if (source.kind === "document") {
            const artifactId = keys[index]?.documentArtifactId ?? "";
            if (!documents.some((entry) => entry.artifactId === artifactId)) {
                documents.push({ artifactId, name: source.documentName });
            }
            continue;
        }
        // Keyed on the timestamp, not the name: a clarification closed before close attribution
        // was recorded names nobody, and that is still the source the item was settled by.
        if (authoredAt !== undefined)
            continue;
        authorUserId = keys[index]?.personUserId;
        if (source.kind === "decision") {
            authorName = source.decidedByName;
            authoredAt = source.decidedAt;
        }
        else if (source.kind === "clarification") {
            authorName = source.closedByName;
            authoredAt = source.closedAt;
        }
        else {
            authorName = source.authorName;
            authoredAt = source.authoredAt;
        }
    }
    return {
        kinds,
        documents,
        ...(authorName !== undefined ? { authorName } : {}),
        ...(authorUserId !== undefined ? { authorUserId } : {}),
        ...(authoredAt !== undefined ? { authoredAt } : {}),
    };
}
